import * as THREE from 'three'

import { items } from '../../shared/Items'
import { PIXEL_SIZE } from '../../shared/shared'
import { queryUseTargets, queryInteract } from '../../shared/Targeting'
import { EQUIP_SLOTS, isHotSlot } from '../../shared/inventories'
import { isTouch } from '../utils'
import { AimVisual } from '../AimVisual'

const AIM_DRAG_THRESHOLD = 15 // pixels of drag before tap becomes an aimed shot
const AIM_TOUCH_SENSITIVITY = 2.0 // multiplier: drag fewer pixels to reach max aim range
const v1 = new THREE.Vector2()
const v2 = new THREE.Vector2()

export class ClientController {
  constructor(world) {
    this.world = world
    this.player = null
    this._dir = new THREE.Vector2() // movement/facing direction
    this._aimDir = new THREE.Vector2() // explicit aim direction (mouse or right stick)
    this._isAiming = false // true when aim direction is actively driven
    this._lastSend = 0
    this._moveMsg = { type: 'playerMove', x: 0, y: 0, d: null, l: null }
    this.interactAction = { type: null, label: null, targets: [], item: null }
    this.nextInteractAction = { type: null, label: null, targets: [], item: null }
    this._stick = null // { id, origin: Vector2 } — left/move joystick (touch)
    this._aimStick = null // { id, origin: Vector2, dragged: bool } — right/aim joystick (touch)
    this._shouldFire = false // fire in _aimDir on next frame (after targeting)
    this._shouldAutoFire = false // auto-fire in _dir on next frame (tap without drag)
    this._actionCooldownAt = 0
    this._aimDist = 0
    this._aimVisual = null
    this._interactFrame = null
    this._activity = null // { locomotion: string, targetId } while a timed interact (fishing etc) is in progress
  }

  attach(player) {
    this.player = player
    this.world.player = player
    // interaction probe
    const pos = player.getPosition()
    this._probe = this.world.physics.add(pos.x, pos.y, {
      type: 'probe',
      trigger: true,
      x: -4 * PIXEL_SIZE,
      y: 4 * PIXEL_SIZE,
      w: 8 * PIXEL_SIZE,
      h: 8 * PIXEL_SIZE,
    })
    this.world.setCameraTarget(pos.x, pos.y + 1, 0)
    this.world.emit('interact', this.interactAction)
    this._aimVisual = new AimVisual(this.world.scene)
  }

  detach(player) {
    if (this.player !== player) return
    this.player = null
    this._probe?.destroy()
    this._probe = null
    this._stick = null
    this._aimStick = null
    this._isAiming = false
    this._shouldFire = false
    this._shouldAutoFire = false
    this._activity = null
    this._aimVisual?.destroy()
    this._aimVisual = null
    this._interactFrame?.destroy()
    this._interactFrame = null
    this.interactAction = { type: null, label: null, targets: [], item: null }
    this.nextInteractAction = { type: null, label: null, targets: [], item: null }
    this.world.emit('interact', this.interactAction)
  }

  activateHotbarSlot(idx) {
    if (!isHotSlot(idx)) return
    const now = performance.now() / 1000
    if (now < (this._actionCooldownAt ?? -Infinity)) return
    const inv = this.world.inventory.data
    if (!inv) return
    const slot = inv.slots[idx]
    const item = items[slot?.id]
    if (!item) return
    if (item.consumable) {
      this._actionCooldownAt = now + item.consumable.cooldown
      this.world.send({ type: 'useHotbar', idx })
    }
  }

  moveInterface(data) {
    this.world.inventory.moveInterfaceItems(data)
  }

  takeAllInterface() {
    this.world.send({ type: 'interfaceTakeAll' })
  }

  craftItem(recipeId) {
    this.world.send({ type: 'interfaceCraft', recipeId })
    const pos = this.player?.getPosition()
    if (pos) this.world.audio.play({ name: 'craft', pitch: 0.05, volume: 0.8 }, pos.x, pos.y)
  }

  closeInterface() {
    this.world.interface = null
    this.world.emit('interface', null)
    this.world.send({ type: 'interfaceClose' })
  }

  performUse() {
    if (this.world.interface) return this.closeInterface()
    this._cancelActivity()
    const now = performance.now() / 1000
    if (now < (this._actionCooldownAt ?? -Infinity)) return

    const slot = this.world.inventory.data?.slots[EQUIP_SLOTS.rItem]
    const item = items[slot?.id] || null
    const use = item?.use || this.player.data.unarmed

    // Client-side energy gate
    if (use.energy && (this.player.data.combat?.energy ?? 0) < use.energy) return

    const dir =
      this._isAiming && this._aimDir.lengthSq() > 0.001
        ? this._aimDir
        : this._dir.lengthSq() > 0.001
          ? this._dir
          : { x: 0, y: -1 }

    this._actionCooldownAt = now + (use.cooldown || 0.3)
    this._faceDir(this.player, dir)
    this.player.playEmote(use.emote)
    if (use.sound) {
      const pos = this.player.getPosition()
      this.world.audio.play(use.sound, pos.x, pos.y)
    }

    if (use?.aim?.shape === 'circle_aimed') {
      const pos = this.player.getPosition()
      const dist = Math.min(Math.max(this._aimDist, use.aim.minRange ?? 1), use.aim.maxRange ?? 6)
      const tx = pos.x + dir.x * dist
      const ty = pos.y + (pos.yBase || 0) + dir.y * dist
      this._spawnProjectileVfx(use, dir, item, tx, ty)
      this.world.send({ type: 'use', dx: dir.x, dy: dir.y, tx, ty })
      if (use.immediate) this._spawnImmediateVfx(use, null, tx, ty)
    } else {
      this._spawnProjectileVfx(use, dir, item)
      this.world.send({ type: 'use', dx: dir.x, dy: dir.y })
      if (use.immediate) {
        const pos = this.player.getPosition()
        const checkBlocked = (x1, y1, x2, y2, e) => this.player.isBlocked(x1, y1, x2, y2, e)
        const isHeal = !!(use.immediate?.heal)
        const allCandidates = this.world.targets.query(pos.x, pos.y + pos.yBase, this._getQueryRange(use))
        const candidates = isHeal ? allCandidates : allCandidates.filter(e => e.value !== this.player)
        const targets = queryUseTargets({
          x: pos.x,
          y: pos.y + pos.yBase,
          direction: dir,
          attack: use,
          candidates,
          item,
          isBlocked: checkBlocked,
        })
        this._spawnImmediateVfx(use, targets)
      }
    }
  }

  performInteract() {
    if (this.world.interface) return this.closeInterface()
    if (!this.interactAction?.type) return
    if (this._activity) return
    const target = this.interactAction.targets[0]
    this.world.send({ type: 'interact', targetId: target?.id })
    if (target) {
      const tb = target.getPosition()
      const pb = this.player.getBase()
      const dx = tb.x - pb.x
      const dy = tb.y - pb.y
      if (dx * dx + dy * dy > 0.001) this._faceDir(this.player, { x: dx, y: dy })
    }
    const info = target?.getInteractInfo?.()
    if (info?.duration && info?.locomotion) {
      this._activity = { locomotion: info.locomotion, targetId: target.id }
      this.player.setLocomotion(info.locomotion)
      this._interactFrame?.destroy()
      this._interactFrame = null
    }
  }

  // Fire at the nearest enemy in any direction; if none in range, fire in facing direction
  performAutoFire() {
    if (this.world.interface) return this.closeInterface()
    this._cancelActivity()
    const now = performance.now() / 1000
    if (now < (this._actionCooldownAt ?? -Infinity)) return

    const slot = this.world.inventory.data?.slots[EQUIP_SLOTS.rItem]
    const item = items[slot?.id] || null

    const target = this._queryNearestTarget(item)
    const use = item?.use || this.player.data.unarmed

    // Client-side energy gate
    if (use.energy) {
      if ((this.player.data.combat?.energy ?? 0) < use.energy) return
    }

    this._actionCooldownAt = now + use.cooldown

    // Direction: toward nearest target, or fall back to movement facing
    const pos = this.player.getPosition()
    let dir, targetDist
    if (target) {
      const tc = target.getCenter()
      const dx = tc.x - pos.x
      const dy = tc.y - (pos.y + (pos.yBase || 0))
      const len = Math.sqrt(dx * dx + dy * dy)
      dir = len > 0.001 ? { x: dx / len, y: dy / len } : { x: 0, y: -1 }
      targetDist = len
    } else {
      dir = this._dir.lengthSq() > 0.001 ? this._dir : { x: 0, y: -1 }
    }

    this._faceDir(this.player, dir)
    this.player.playEmote(use.emote)
    if (use.sound) this.world.audio.play(use.sound, pos.x, pos.y)

    if (use?.aim?.shape === 'circle_aimed') {
      const rawDist = targetDist ?? use.aim.maxRange ?? 6
      const dist = Math.min(Math.max(rawDist, use.aim.minRange ?? 0), use.aim.maxRange ?? 6)
      const tx = pos.x + dir.x * dist
      const ty = pos.y + (pos.yBase || 0) + dir.y * dist
      this._spawnProjectileVfx(use, dir, item, tx, ty)
      this.world.send({ type: 'use', dx: dir.x, dy: dir.y, tx, ty })
      if (use.immediate) this._spawnImmediateVfx(use, null, tx, ty)
    } else {
      this._spawnProjectileVfx(use, dir, item)
      this.world.send({ type: 'use', dx: dir.x, dy: dir.y })
      if (use.immediate) this._spawnImmediateVfx(use, target ? [target] : [])
    }
  }

  // Spawn impact sprites for immediate-hit weapons
  // targets: array of entities (trap/circle_self), or null for circle_aimed (use aimX/aimY instead)
  _spawnImmediateVfx(use, targets, aimX, aimY) {
    const impact = use.immediate?.impactSprite
    if (!impact?.url) return
    const spawn = (x, y) => {
      const s = this.world.sprites.add({
        url: impact.url,
        fps: impact.fps,
        width: impact.width || 1,
        height: impact.height || 1,
        x,
        y,
        yCenter: impact.yCenter,
        loop: false,
        onEnd: () => s.destroy(),
      })
    }
    if (aimX != null) {
      spawn(aimX, aimY)
    } else if (targets?.length) {
      for (const t of targets) {
        const c = t.getCenter?.()
        if (c) spawn(c.x, c.y)
      }
    }
  }

  // Spawn local flight sprites for projectile weapons
  _spawnProjectileVfx(use, dir, item, tx, ty) {
    if (!use?.projectiles?.length) return
    const from = this.player.getCenter()
    const aimAngle = Math.atan2(dir.y, dir.x)
    for (const proj of use.projectiles) {
      const angle = aimAngle + ((proj.offsetDegrees || 0) * Math.PI) / 180
      const pdx = Math.cos(angle)
      const pdy = Math.sin(angle)
      if (proj.arc && tx != null) {
        const dx = tx - from.x,
          dy = ty - from.y
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.01
        this.world.projectiles.spawn({
          sx: from.x,
          sy: from.y,
          pdx: dx / dist,
          pdy: dy / dist,
          maxDist: dist,
          ex: tx,
          ey: ty,
          arc: proj.arc,
          speed: proj.speed || 5,
          flightSprite: proj.flightSprite,
          impactSprite: proj.impactSprite,
          shadowSprite: proj.shadowSprite,
          item,
          shooter: this.player,
        })
      } else {
        const end = this._projectileWallEndpoint(from, pdx, pdy, proj.maxDistance)
        this.world.projectiles.spawn({
          sx: from.x,
          sy: from.y,
          pdx,
          pdy,
          maxDist: end.maxDist,
          ex: end.x,
          ey: end.y,
          delay: proj.delay || 0,
          projRadius: proj.radius || 0.15,
          maxTargets: proj.maxTargets ?? 1,
          speed: proj.speed || 10,
          flightSprite: proj.flightSprite,
          impactSprite: proj.impactSprite,
          shadowSprite: proj.shadowSprite,
          item,
          shooter: this.player,
        })
      }
    }
  }

  // Find the closest hittable target in any direction within weapon range
  _queryNearestTarget(item) {
    const pos = this.player.getPosition()
    const px = pos.x
    const py = pos.y + (pos.yBase || 0)
    const use = item?.use || this.player.data.unarmed
    const range = this._getQueryRange(use)
    const candidates = this.world.targets.query(px, py, range)
    const checkBlocked = (x1, y1, x2, y2, entity) => this.player.isBlocked(x1, y1, x2, y2, entity)

    let best = null
    let bestDist = Infinity
    for (const entry of candidates) {
      const entity = entry.value
      if (entity === this.player) continue // face toward nearest other target, not self
      if (!entity.isHitBy?.(item)) continue
      const dx = entry.x - px
      const dy = entry.y - py
      const dist = Math.sqrt(dx * dx + dy * dy)
      const effectiveDist = Math.max(0, dist - entry.radius)
      if (effectiveDist > range) continue
      if (checkBlocked(px, py, entry.x, entry.y, entity)) continue
      if (effectiveDist < bestDist) {
        bestDist = effectiveDist
        best = entity
      }
    }
    return best
  }

  _getQueryRange(use) {
    const shape = use?.aim?.shape || 'trap'
    return use?.projectiles?.length
      ? Math.max(...use.projectiles.map(p => p.maxDistance))
      : shape === 'circle_aimed'
        ? (use.aim?.maxRange ?? 6)
        : shape === 'circle_self'
          ? use.aim?.radius || 1.5
          : (use?.aim?.farDist ?? 1.5)
  }

  // Find where a projectile ray stops at a wall (walls are static, checked once at fire time)
  _projectileWallEndpoint(from, pdx, pdy, maxDist) {
    const ex = from.x + pdx * maxDist
    const ey = from.y + pdy * maxDist
    const wallHit = this.player.isBlocked(from.x, from.y, ex, ey, null)
    if (wallHit) {
      const d = Math.sqrt((wallHit.x - from.x) ** 2 + (wallHit.y - from.y) ** 2)
      return { x: wallHit.x, y: wallHit.y, maxDist: d }
    }
    return { x: ex, y: ey, maxDist }
  }

  onActivityReject(entityId) {
    if (this._activity?.targetId !== entityId) return
    this._clearActivity()
  }

  onActivityEnd(entityId) {
    if (this._activity?.targetId !== entityId) return
    this._clearActivity()
  }

  _clearActivity() {
    this._activity = null
    this.player.setLocomotion('idle')
    this.interactAction.targets = [] // nudge diff so frame re-appears next frame if still in range
  }

  _cancelActivity() {
    if (!this._activity) return false
    this.world.send({ type: 'interactCancel', targetId: this._activity.targetId })
    this._clearActivity()
    return true
  }

  // Set player facing from a direction vector
  _faceDir(player, vec) {
    if (Math.abs(vec.x) >= Math.abs(vec.y)) {
      player.face(vec.x > 0 ? 'east' : 'west')
    } else {
      player.face(vec.y > 0 ? 'north' : 'south')
    }
  }

  update(delta) {
    const player = this.player
    if (!player) return

    const input = this.world.input
    const dead = player.data.combat?.dead

    if (!dead) {
      this.updateMovement(player, input, delta)
      this.updateAiming(player, input)
      this.updateFootsteps(player, delta)
    }

    this.updateTargeting(player, input)

    if (!dead) {
      // Drag-aim release → directional fire
      if (this._shouldFire) {
        this._shouldFire = false
        this.performUse()
      }
      // Tap / RMB → auto-fire nearest enemy in any direction
      if (this._shouldAutoFire) {
        this._shouldAutoFire = false
        this.performAutoFire()
      }
      // Keyboard
      if (!this.world.editor.open && input.key('Space').pressed) this._shouldAutoFire = true
      if (!this.world.editor.open && input.key('KeyE').pressed) this.performInteract()
      for (let i = 0; i < 4; i++) {
        const key = input.key(`Digit${i + 1}`)
        if (key.pressed) {
          this.activateHotbarSlot(i)
          this.world.emit('hotbarHighlight', i)
        }
        if (key.released) this.world.emit('hotbarHighlight', null)
      }
    }

    const pos = player.getPosition()
    this._probe?.setPosition(pos.x, pos.y)
    this.world.setCameraTarget(pos.x, pos.y + 1)

    this._lastSend += delta
    if (this._lastSend > 1 / 10) {
      this._lastSend -= 1 / 10
      this._moveMsg.x = pos.x
      this._moveMsg.y = pos.y
      this._moveMsg.d = player.getDirection()
      this._moveMsg.l = player.getLocomotion()
      this.world.send(this._moveMsg)
    }
  }

  updateFootsteps(player, delta) {
    const walking = player.getLocomotion() === 'walk'
    if (!walking) {
      this._footstepTimer = 0
      return
    }
    this._footstepTimer = (this._footstepTimer ?? 0) - delta
    if (this._footstepTimer > 0) return
    this._footstepTimer = 0.4 // seconds between steps — swap for tile-based sound later
    const pos = player.getPosition()
    const sound = { name: 'footstep-basic', pitch: 0.08, volume: 0.1 }
    this.world.audio.play(sound, pos.x, pos.y)
  }

  updateMovement(player, input, delta) {
    const moveSpeed = player.data.movement.speed + (player.data.stats?.speed || 0)

    if (isTouch) {
      // New touches on LEFT half → move joystick
      for (const t of input.touchesStarted) {
        if (!this._stick && t.x < this.world.width / 2) {
          this._stick = { id: t.id, origin: new THREE.Vector2(t.x, t.y) }
        }
      }
      // Release move joystick
      for (const t of input.touchesEnded) {
        if (this._stick?.id === t.id) this._stick = null
      }
      // Apply movement
      if (this._stick) {
        const touch = input.touches.get(this._stick.id)
        if (touch) {
          const diff = v1.set(touch.position.x - this._stick.origin.x, -(touch.position.y - this._stick.origin.y))
          if (diff.lengthSq() > 0.001) this._dir.copy(diff).normalize()
          if (diff.lengthSq() > 0.001) this._cancelActivity()
          if (!player.isEmotePlaying() && !this._activity) {
            const move = v2
              .copy(diff)
              .normalize()
              .multiplyScalar(moveSpeed * delta)
            this.applyMove(player, move.x, move.y)
          }
        }
      } else if (!this._activity) {
        player.setLocomotion('idle')
      }
      return
    }

    // Keyboard movement (desktop)
    const move = v1.set(0, 0)
    if (input.key('KeyW').down || input.key('ArrowUp').down) move.y += 1
    if (input.key('KeyS').down || input.key('ArrowDown').down) move.y -= 1
    if (input.key('KeyA').down || input.key('ArrowLeft').down) move.x -= 1
    if (input.key('KeyD').down || input.key('ArrowRight').down) move.x += 1
    if (move.lengthSq() > 0.001) this._dir.copy(move).normalize()
    if (move.lengthSq() > 0.001) this._cancelActivity()
    if (!player.isEmotePlaying() && !this._activity) {
      move.normalize().multiplyScalar(moveSpeed * delta)
      this.applyMove(player, move.x, move.y)
    }
  }

  applyMove(player, dx, dy) {
    player.move(dx, dy)
    player.setLocomotion(dx !== 0 || dy !== 0 ? 'walk' : 'idle')
  }

  updateAiming(player, input) {
    if (isTouch) {
      // New touches on RIGHT half → aim joystick
      for (const t of input.touchesStarted) {
        if (!this._aimStick && t.x >= this.world.width / 2) {
          this._aimStick = { id: t.id, origin: new THREE.Vector2(t.x, t.y), dragged: false }
        }
      }

      // Update aim direction while stick is held
      if (this._aimStick) {
        const touch = input.touches.get(this._aimStick.id)
        if (touch) {
          const diff = v1.set(touch.position.x - this._aimStick.origin.x, -(touch.position.y - this._aimStick.origin.y))
          if (diff.length() > AIM_DRAG_THRESHOLD) this._aimStick.dragged = true
          if (this._aimStick.dragged && diff.lengthSq() > 0.001) {
            this._aimDir.copy(diff).normalize()
            this._isAiming = true
            const worldPerPixel = (this.world.camera.top * 2) / this.world.height
            this._aimDist = diff.length() * worldPerPixel * AIM_TOUCH_SENSITIVITY
          }
        }
      } else {
        this._isAiming = false
      }

      // Release aim stick → schedule fire
      // NOTE: _isAiming stays true this frame so targeting uses the aim direction
      for (const t of input.touchesEnded) {
        if (this._aimStick?.id === t.id) {
          if (this._aimStick.dragged) {
            this._shouldFire = true
          } else {
            this._shouldAutoFire = true
          }
          this._aimStick = null
        }
      }
    } else {
      // Desktop: always track mouse aim direction
      const pos = player.getPosition()
      const mouseWorld = this.world.getWorldPointerPosition()
      const dx = mouseWorld.x - pos.x
      const dy = mouseWorld.y - (pos.y + (pos.yBase || 0))
      const lenSq = dx * dx + dy * dy
      if (lenSq > 0.0001) {
        const len = Math.sqrt(lenSq)
        this._aimDir.set(dx / len, dy / len)
      }
      this._aimDist = Math.sqrt(lenSq)
      // LMB held → aiming (show indicator); release → fire
      if (input.lmb.down || input.lmb.released) {
        this._isAiming = true
      } else {
        this._isAiming = false
      }
      if (input.lmb.released && !this.world.editor.open) {
        this._shouldFire = true
      }
      // RMB → auto-fire nearest enemy
      if (input.rmb.pressed && !this.world.editor.open) {
        this._shouldAutoFire = true
      }
    }

    // Facing follows movement, unless an attack emote is playing (hold fire direction for its duration)
    if (!player.isEmotePlaying() && player.getLocomotion() === 'walk' && this._dir.lengthSq() > 0.001) {
      this._faceDir(player, this._dir)
    }
  }

  updateTargeting(player, input) {
    const slot = this.world.inventory.data?.slots[EQUIP_SLOTS.rItem]
    const item = items[slot?.id] || null
    const pos = player.getPosition()
    const px = pos.x
    const py = pos.y + pos.yBase

    const dir =
      this._isAiming && this._aimDir.lengthSq() > 0.001
        ? this._aimDir
        : this._dir.lengthSq() > 0.001
          ? this._dir
          : player.getDirection()

    const use = item?.use || this.player.data.unarmed
    const queryRange = this._getQueryRange(use)
    const candidates = this.world.targets.query(px, py, queryRange)
    const checkBlocked = (x1, y1, x2, y2, targetEntity) => player.isBlocked(x1, y1, x2, y2, targetEntity)

    // ── interact action ──
    const interact = queryInteract({
      x: px,
      y: py,
      radius: this.player.data.target?.radius ?? 0,
      direction: dir,
      candidates,
      checkAction: entity => entity.canInteract?.() ?? false,
    })

    if (interact) {
      const info = interact.target.getInteractInfo()
      this.nextInteractAction.type = 'interact'
      this.nextInteractAction.label = info.label
      this.nextInteractAction.targets = [interact.target]
      this.nextInteractAction.item = item
    } else {
      this.nextInteractAction.type = null
      this.nextInteractAction.label = null
      this.nextInteractAction.targets = []
      this.nextInteractAction.item = null
    }

    if (
      this.interactAction.type !== this.nextInteractAction.type ||
      this.interactAction.targets[0] !== this.nextInteractAction.targets[0]
    ) {
      this.interactAction = { ...this.nextInteractAction }
      this.world.emit('interact', this.interactAction)
      // on non-touch devices, show an interact frame above the target
      this._interactFrame?.destroy()
      this._interactFrame = null
      if (this.interactAction.type && this.interactAction.targets[0] && !this._activity) {
        const target = this.interactAction.targets[0]
        const pos = target.data?.position
        if (pos) {
          this._interactFrame = this.world.frames.create({
            type: 'interact',
            wx: pos.x + (target.data.target?.ox ?? 0),
            wy: pos.y + (target.data.target?.oy ?? 0) + 8 * PIXEL_SIZE,
          })
        }
      }
    }

    // ── aim visual (shown when actively drag-aiming) ──
    if (this._aimVisual) {
      if (this._isAiming) {
        let aimX = px,
          aimY = py
        if (use?.aim?.shape === 'circle_aimed') {
          const clampedDist = Math.min(Math.max(this._aimDist, use.aim.minRange ?? 1), use.aim.maxRange ?? 6)
          aimX = px + dir.x * clampedDist
          aimY = py + dir.y * clampedDist
        }
        this._aimVisual.update({ x: px, y: py, direction: dir, attack: use, aimX, aimY })
      } else {
        this._aimVisual.clear()
      }
    }
  }
}
